/** Video state -> plain words (the status line on My videos and the stage rail). */
import type { PlanCode, Video, VideoSegment } from "../api/types";
import { fmtExpiry, fmtSpan, fmtWait, plural } from "./format";

export type Stage = 0 | 1 | 2 | 3; // Uploaded | Being set up | Finding every ball | Clips ready
export const STAGES = ["Uploaded", "Being set up", "Finding every ball", "Clips ready"] as const;

export interface Status {
  headline: string;   // "Being set up."
  words: string;      // status column on the card: "Being set up"
  detail: string;     // second half of the card line: "by Wednesday"
  dot: "" | "on" | "ok" | "bad";
  stage: Stage | -1;  // -1 = failed / unusable / expired (no stage)
  progress: number;   // 0..1 for the hairline
  terminal: boolean;  // no more polling
  explain: string;    // the paragraph under the rail
  action: "none" | "clips" | "retry" | "plans";
}

/** Why a video cannot be measured, in words, by the API's error code. */
const UNUSABLE_DETAIL: Record<string, string> = {
  CAMERA_MOVED: "the camera moved",
  STUMPS_MOVED: "the stumps moved",
  STUMPS_NOT_FOUND: "stumps not found",
};

export function isReady(v: Video): boolean {
  return v.state === "ready";
}

/**
 * The parts of a refused upload the user may have analysed instead (POST
 * /v1/videos/{id}/segment): where the camera stayed still, from the camera
 * check. Empty unless the API offers the choice.
 */
export function videoParts(v: Video): VideoSegment[] {
  if (!v.allowed_actions.includes("choose_segment")) return [];
  const segs = (v.input_check as { drift?: { segments?: VideoSegment[] } } | null)?.drift?.segments;
  return Array.isArray(segs) ? segs : [];
}

export function videoStatus(v: Video, plan: PlanCode, now = new Date()): Status {
  const wait = fmtWait(v.expected_wait_s, now);
  switch (v.state) {
    case "uploading":
      return { headline: "Uploading.", words: "Uploading", detail: "keep this page open or come back to resume", dot: "on", stage: 0, progress: 0.05, terminal: false, explain: "The upload is not finished. Open Upload and choose the same file to resume it.", action: "none" };
    case "uploaded":
    case "ingesting":
      return { headline: "Checking the video.", words: "Checking the video", detail: "a few minutes", dot: "on", stage: 0, progress: 0.15, terminal: false, explain: v.segment ? `We make a working copy of ${fmtSpan(v.segment)} and check the camera stayed still.` : "We make a working copy and check the camera did not move.", action: "none" };
    case "calibration_queue":
    case "calibrating":
      // Autocal waits for the same machine as the analysis, so it has the same wait.
      return { headline: "Being set up.", words: "Being set up", detail: wait || (plan === "free" ? "up to 3 days" : "soon"), dot: "on", stage: 1, progress: 0.35, terminal: false, explain: "We find the stumps in your video and mark the pitch from them, so speed and length can be measured. A camera you have used before skips this step.", action: plan === "free" ? "plans" : "none" };
    case "calibrated":
    case "queued":
      return plan === "free"
        ? { headline: "Waiting for a free slot.", words: "Waiting for a free slot", detail: wait || "up to 3 days", dot: "", stage: 2, progress: 0.5, terminal: false, explain: "Free videos run when the machine is idle, usually within 3 days. We email you when the clips are ready.", action: "plans" }
        : { headline: "In the queue.", words: "In the queue", detail: wait || "soon", dot: "on", stage: 2, progress: 0.5, terminal: false, explain: "Your video is next in line. We email you when the clips are ready.", action: "none" };
    case "processing":
      return { headline: "Finding every ball.", words: "Finding every ball", detail: "about an hour", dot: "on", stage: 2, progress: 0.7, terminal: false, explain: "Every delivery is being found and measured. We email you when the clips are ready.", action: "none" };
    case "held":
      // A safeguard stopped the run before publishing (apps/api/src/services/review.ts); an operator decides.
      return { headline: "Checking the results.", words: "Checking the results", detail: "", dot: "on", stage: 2, progress: 0.9, terminal: true, explain: v.error?.message ?? "We are checking this analysis before it goes out. You will get an email when it is ready.", action: "none" };
    case "ready":
      return { headline: "Clips ready.", words: "Ready", detail: [v.clip_count != null ? plural(v.clip_count, "clip") : "", fmtExpiry(v.expires_at, now)].filter(Boolean).join(" · "), dot: v.expires_at && new Date(v.expires_at).getTime() - now.getTime() < 3 * 86_400_000 ? "ok" : "on", stage: 3, progress: 1, terminal: true, explain: "", action: "clips" };
    case "ingest_failed":
      // The file was read; the camera moved. The API's message names the two compared moments, the shift and the limit.
      if (v.error?.code === "INPUT_CHECK_FAILED") {
        return { headline: "The camera moved.", words: "Camera moved", detail: "", dot: "bad", stage: -1, progress: 0, terminal: true, explain: v.error.message ?? "The camera moved during the recording.", action: "retry" };
      }
      return { headline: "We could not read this file.", words: "Could not read the file", detail: v.error?.code ?? "", dot: "bad", stage: -1, progress: 0, terminal: true, explain: v.error?.message ?? "The video could not be decoded. Try an MP4 or MOV straight from the camera.", action: "retry" };
    case "unusable":
      return { headline: "This video cannot be measured.", words: "Cannot be measured", detail: UNUSABLE_DETAIL[v.error?.code ?? ""] ?? (v.error?.code ?? ""), dot: "bad", stage: -1, progress: 0, terminal: true, explain: v.error?.message ?? "The pitch could not be marked on this video. It needs a fixed camera with both stumps visible.", action: "retry" };
    case "failed":
      return { headline: "Something went wrong.", words: "Did not finish", detail: v.error?.code ?? "", dot: "bad", stage: -1, progress: 0, terminal: true, explain: v.error?.message ?? "The analysis did not finish. This video does not count against your month.", action: "retry" };
    case "expired":
      return { headline: "Expired.", words: "Expired", detail: "clips removed", dot: "", stage: -1, progress: 0, terminal: true, explain: "The clips were kept for the plan's retention period and have been removed.", action: "none" };
    default:
      return { headline: v.state, words: v.state, detail: "", dot: "", stage: -1, progress: 0, terminal: true, explain: "", action: "none" };
  }
}
